import { useRef } from 'react'
import { Id, toast, ToastOptions, UpdateOptions } from 'react-toastify'
import i18n from 'i18next'
import { useAtomValue } from 'jotai'

import { EFinality } from '@/lib/solana/types'
import { preferredExplorerAtom } from '@/stores/settings'

export { EFinality, toast }

export function useToastTransaction() {
  const tidRef = useRef<Id>()
  const preferredExplorer = useAtomValue(preferredExplorerAtom)
  const defaultOption = {
    onClose: () => {
      tidRef.current = undefined
    }
  }

  // 更新为processing状态
  const processing = (options?: UpdateOptions) => {
    const render = (
      <div className="min-w-[120px] max-w-[300px]">
        <h6 className="text-sm">
          {i18n.t('trade.message.transactionProcessing')}
        </h6>
      </div>
    )
    const opts = {
      ...defaultOption,
      type: 'default',
      closeOnClick: true,
      isLoading: true,
      ...options
    } as ToastOptions
    let id = options?.toastId ?? options?.updateId ?? tidRef.current
    if (id) {
      toast.update(id, { render, ...opts })
    } else {
      id = toast(render, opts)
    }
    tidRef.current = id
  }

  // 更新为confirmed状态
  const confirmed = (
    options?: UpdateOptions & {
      txHash?: string
      message?: string
      messageDesc?: string
    }
  ) => {
    const { txHash, message, messageDesc, ...rest } = options || {}
    const messageText = message ?? i18n.t('trade.message.transactionSubmited')
    const messageDescText =
      messageDesc ?? i18n.t('trade.message.transactionConfirming')
    const render = (
      <div className="flex min-w-[120px] max-w-[300px] items-center">
        <div className="flex-1">
          {messageText && <h6 className="text-sm">{messageText}</h6>}
          {messageDescText && (
            <p className="whitespace-pre-wrap text-sm opacity-60">
              {messageDescText}
            </p>
          )}
        </div>
        {txHash && (
          <a
            className="mx-4 rounded-md bg-primary px-2 py-1 text-sm"
            href={`${preferredExplorer}/tx/${txHash}`}
            target="_blank"
            onClick={(e) => e.stopPropagation()}
          >
            {i18n.t('trade.message.ViewTx')}
          </a>
        )}
      </div>
    )

    const opts = {
      ...defaultOption,
      type: 'default',
      closeOnClick: false,
      closeButton: true,
      isLoading: true,
      ...rest
    } as ToastOptions

    let id = options?.toastId ?? options?.updateId ?? tidRef.current
    if (id) {
      toast.update(id, { render, ...opts })
    } else {
      id = toast(render, opts)
    }
    tidRef.current = id
  }

  // 更新为finalized状态
  const finalized = (
    options?: UpdateOptions & {
      txHash?: string
      message?: string
      messageDesc?: string
    }
  ) => {
    const { txHash, message, messageDesc, ...rest } = options || {}
    const messageText = message ?? i18n.t('trade.message.transactionSucceed')
    const messageDescText = messageDesc
    const render = (
      <div className="flex min-w-[120px] max-w-[300px] items-center">
        <div className="flex-1">
          {messageText && <h6 className="text-sm">{messageText}</h6>}
          {messageDescText && (
            <p className="whitespace-pre-wrap text-sm opacity-60">
              {messageDescText}
            </p>
          )}
        </div>
        {txHash && (
          <a
            className="mx-4 rounded-md bg-primary px-2 py-1 text-sm"
            href={`${preferredExplorer}/tx/${txHash}`}
            target="_blank"
            onClick={(e) => e.stopPropagation()}
          >
            {i18n.t('trade.message.ViewTx')}
          </a>
        )}
      </div>
    )
    const opts = {
      ...defaultOption,
      type: 'success',
      autoClose: 5000,
      closeOnClick: false,
      closeButton: true,
      isLoading: false,
      ...rest
    } as ToastOptions
    let id = options?.toastId ?? options?.updateId ?? tidRef.current
    if (id) {
      toast.update(id, { render, ...opts })
    } else {
      id = toast(render, opts)
    }
    tidRef.current = id
  }

  // 更新为failed状态
  const failed = (
    options?: UpdateOptions & {
      txHash?: string
      message?: string
      messageDesc?: string
      error?: any
    }
  ) => {
    const { txHash, error, message, messageDesc, ...rest } = options || {}
    const messageText = message ?? i18n.t('trade.message.transactionFailed')
    let messageDescText = messageDesc
    if (!messageDescText) {
      const errorString = error?.message || error?.msg || error
      messageDescText =
        (errorString?.match(/^(.+?):.*$/) || [])[1]?.trim() || errorString
      if (/Error:\s*\d+/i.test(errorString)) {
        messageDescText = `${messageDescText ? messageDescText + '\n' : ''}${i18n.t('trade.message.changeRpcUrl')}`
      }
    }

    const render = (
      <div className="flex min-w-[120px] max-w-[300px] items-center">
        <div className="flex-1">
          {messageText && <h6 className="text-sm">{messageText}</h6>}
          {messageDescText && (
            <p className="whitespace-pre-wrap text-sm opacity-60">
              {messageDescText}
            </p>
          )}
        </div>
        {txHash && (
          <a
            className="mx-4 rounded-md bg-favorite px-2 py-1 text-sm"
            style={{ display: txHash ? 'block' : 'none' }}
            href={`${preferredExplorer}/tx/${txHash}`}
            target="_blank"
            onClick={(e) => e.stopPropagation()}
          >
            {i18n.t('trade.message.ViewTx')}
          </a>
        )}
      </div>
    )
    const opts = {
      ...defaultOption,
      type: 'error',
      closeOnClick: false,
      closeButton: true,
      isLoading: false,
      ...rest
    } as ToastOptions
    let id = options?.toastId ?? options?.updateId ?? tidRef.current
    if (id) {
      toast.update(id, { render, ...opts })
    } else {
      id = toast(render, opts)
    }
    tidRef.current = id
  }

  const callback = {
    confirmed: {
      finality: EFinality.CONFIRMED,
      onSend: () => {
        processing()
      },
      onConfirmed: (txHash: string, _txStatus: any) => {
        finalized({ txHash })
      },
      onError: (error: any, txHash: string, _txStatus: any): any => {
        failed({ txHash, message: error?.message || error })
      }
    },
    finalized: {
      finality: EFinality.FINALIZED,
      onSend: () => {
        processing()
      },
      onConfirmed: (txHash: string, _txStatus: any) => {
        confirmed({ txHash })
      },
      onFinalized: (txHash: string, _txStatus: any) => {
        finalized({ txHash })
      },
      onError: (error: any, txHash: string, _txStatus: any) => {
        failed({ txHash, message: error?.message || error })
      }
    }
  }

  return {
    processing,
    confirmed,
    finalized,
    failed,
    callback
  }
}
